'use client'

import useControlled from '@/hooks/use-controlled'
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'

export interface UseTabsProps {
  defaultValue?: string
  mountOnEnter?: boolean
  onValueChange?: (value: string) => void
  unmountOnExit?: boolean
  value?: string
  variant?: 'enclosed' | 'chip'
}

export function useTabs(props: UseTabsProps) {
  const {
    defaultValue,
    mountOnEnter,
    onValueChange,
    unmountOnExit,
    value: valueProp,
    variant = 'enclosed',
  } = props

  const id = useId()

  const [value, setValue] = useControlled({
    value: valueProp,
    defaultValue,
  })

  const cache = useRef(value)
  const handle = useRef<(value: string) => void>()
  handle.current = (value: string) => {
    if (cache.current === value) {
      return
    }

    cache.current = value
    setValue(value)
    onValueChange?.(value)
  }

  const tabListRef = useRef<HTMLDivElement>(null)
  const indicatorRef = useRef<HTMLSpanElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const tabPanelRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useLayoutEffect(() => {
    if (!value) {
      return
    }

    const tabList = tabListRef.current
    const tab = tabRefs.current[value]

    if (!tabList || !tab) {
      return
    }

    const scrollSelectedTabIntoView = () => {
      tabList.scrollTo({
        top: tab.offsetTop + tab.clientHeight / 2 - tabList.clientHeight / 2,
        left: tab.offsetLeft + tab.clientWidth / 2 - tabList.clientWidth / 2,
        behavior: 'instant',
      })
    }

    const updateIndicatorStyle = () => {
      const indicator = indicatorRef.current

      if (indicator) {
        indicator.style.top = `${tab.offsetTop}px`
        indicator.style.left = `${tab.offsetLeft}px`
        indicator.style.width = `${tab.clientWidth}px`
        indicator.style.height = `${tab.clientHeight}px`
      }
    }

    const resizeObserver = new ResizeObserver(() => {
      scrollSelectedTabIntoView()
      updateIndicatorStyle()
    })

    resizeObserver.observe(tabList)

    return () => {
      resizeObserver.disconnect()
    }
  }, [value])

  useLayoutEffect(() => {
    if (!value) {
      return
    }

    const content = contentRef.current
    const tabPanel = tabPanelRefs.current[value]

    if (!content || !tabPanel) {
      return
    }

    content.scrollTo({
      left: tabPanel.offsetLeft,
      behavior: 'instant',
    })
  }, [value])

  useEffect(() => {
    const tabList = tabListRef.current
    const content = contentRef.current

    if (!tabList || !content) {
      return
    }

    let isTouching = false

    const handleScroll = () => {
      const rect = content.getBoundingClientRect()

      const intersections = Object.keys(tabPanelRefs.current)
        .map((key) => {
          const element = tabPanelRefs.current[key]

          let intersectionRatio = 0

          if (element) {
            const elementRect = element.getBoundingClientRect()
            const offset = Math.abs(
              Math.round(content.scrollLeft - element.offsetLeft)
            )
            if (offset < rect.width) {
              intersectionRatio = (elementRect.width - offset) / rect.width
            }
          }

          return { key, intersectionRatio }
        })
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)

      const a = intersections[0]
      const b = intersections[1]

      if (a && b) {
        const tabA = tabRefs.current[a.key]
        const tabB = tabRefs.current[b.key]

        if (tabA && tabB) {
          const width =
            tabA.clientWidth * a.intersectionRatio +
            tabB.clientWidth * b.intersectionRatio
          const height =
            tabA.clientHeight * a.intersectionRatio +
            tabB.clientHeight * b.intersectionRatio
          const top =
            tabA.offsetTop * a.intersectionRatio +
            tabB.offsetTop * b.intersectionRatio
          const left =
            tabA.offsetLeft * a.intersectionRatio +
            tabB.offsetLeft * b.intersectionRatio

          tabList.scrollTo({
            top: top + height / 2 - tabList.clientHeight / 2,
            left: left + width / 2 - tabList.clientWidth / 2,
            behavior: 'instant',
          })

          const indicator = indicatorRef.current

          if (indicator) {
            indicator.style.top = `${top}px`
            indicator.style.left = `${left}px`
            indicator.style.width = `${width}px`
            indicator.style.height = `${height}px`
          }
        }
      }

      if (isTouching || a.intersectionRatio < 1) {
        return
      }

      handle.current?.(a.key)
    }

    const handleTouchStart = () => {
      isTouching = true
    }

    const handleTouchEnd = () => {
      isTouching = false
      handleScroll()
    }

    content.addEventListener('scroll', handleScroll)
    content.addEventListener('touchstart', handleTouchStart)
    content.addEventListener('touchend', handleTouchEnd)

    return () => {
      content.removeEventListener('scroll', handleScroll)
      content.removeEventListener('touchstart', handleTouchStart)
      content.removeEventListener('touchend', handleTouchEnd)
    }
  }, [])

  const handleValueChange = useCallback((value: string) => {
    handle.current?.(value)
  }, [])

  const getTabId = useCallback(
    (value: string) => {
      return `${id}-tab-${value}`
    },
    [id]
  )

  const getTabPanelId = useCallback(
    (value: string) => {
      return `${id}-panel-${value}`
    },
    [id]
  )

  const contextValue = useMemo(
    () => ({
      contentRef,
      getTabId,
      getTabPanelId,
      indicatorRef,
      mountOnEnter,
      onValueChange: handleValueChange,
      tabListRef,
      tabPanelRefs,
      tabRefs,
      unmountOnExit,
      value,
      variant,
    }),
    [
      getTabId,
      getTabPanelId,
      handleValueChange,
      mountOnEnter,
      unmountOnExit,
      value,
      variant,
    ]
  )

  return contextValue
}
